import { NavLink, Outlet } from 'react-router-dom';
import clsx from 'clsx';

interface AdminNavItem {
  to: string;
  label: string;
  end?: boolean;
}

const NAV_ITEMS: AdminNavItem[] = [
  { to: '/admin', label: 'Dashboard', end: true },
  { to: '/admin/products', label: 'Products' },
  { to: '/admin/categories', label: 'Categories' },
  { to: '/admin/discounts', label: 'Discounts' },
  { to: '/admin/stock', label: 'Stock' },
  { to: '/admin/sales', label: 'Sales' },
  { to: '/admin/users', label: 'Users' },
  { to: '/admin/scan-sheet', label: 'Scan sheet' },
  { to: '/admin/settings', label: 'Settings' },
];

/**
 * The admin section's own chrome: a compact horizontally-scrollable sub-nav
 * on phones, a left sidebar on >=768px. Nested inside AppShell, so the top
 * bar (and, on phones, the Sell/Today/Admin bottom tabs) is still present
 * around this. The container's own padding (from .page-container, applied
 * by AppShell's <main>) supplies the horizontal gutters and the grid gap
 * supplies the sidebar/content gutter - this root adds no padding of its
 * own, so the sidebar's left edge lines up with the wordmark.
 */
export function AdminLayout() {
  return (
    <div className="md:grid md:grid-cols-[12rem_minmax(0,1fr)] md:gap-8 md:items-start">
      <nav
        aria-label="Admin sections"
        className="md:hidden sticky-under-header z-20 bg-bg flex gap-2 overflow-x-auto -mx-4 px-4 sm:-mx-6 sm:px-6 py-2 border-b border-border"
      >
        {NAV_ITEMS.map((item) => (
          <AdminNavLink key={item.to} item={item} variant="chip" />
        ))}
      </nav>

      <nav
        aria-label="Admin sections"
        className="hidden md:flex md:flex-col md:gap-1 sticky-under-header md:py-4 lg:py-8"
      >
        {NAV_ITEMS.map((item) => (
          <AdminNavLink key={item.to} item={item} variant="sidebar" />
        ))}
      </nav>

      <div className="min-w-0">
        <Outlet />
      </div>
    </div>
  );
}

function AdminNavLink({ item, variant }: { item: AdminNavItem; variant: 'chip' | 'sidebar' }) {
  if (variant === 'chip') {
    return (
      <NavLink
        to={item.to}
        end={item.end}
        className={({ isActive }) =>
          clsx(
            'shrink-0 min-h-12 px-4 rounded-full font-body font-semibold text-sm border-2 whitespace-nowrap flex items-center',
            isActive ? 'bg-aqua text-teal-dark border-aqua' : 'bg-surface text-muted border-border',
          )
        }
      >
        {item.label}
      </NavLink>
    );
  }

  return (
    <NavLink
      to={item.to}
      end={item.end}
      className={({ isActive }) =>
        clsx(
          'min-h-12 px-4 rounded-lg font-body font-semibold text-sm flex items-center',
          isActive ? 'bg-aqua text-teal-dark' : 'text-muted hover:bg-surface',
        )
      }
    >
      {item.label}
    </NavLink>
  );
}
